import { clamp, damp, lerp, smoothstep } from '../core/math';
import type { StickVector } from './touch';

/**
 * Touch driving, the one scheme (docs/DRIVING.md §8, docs/MOBILE.md): the stick points where you want
 * to GO on the screen and only steers; GAS and BRAKE are two equal pedal zones. Pure and time-stepped,
 * so it is unit-tested (touchDrive.test.ts) and measured in a closed loop against the real handling
 * model; the physics (which the online server validates) is untouched — only what the driver asks for
 * changes. The on-screen layer (src/ui/touchControls.ts) feeds the stick, the pedals and the car's
 * facts in every frame and hands the result to `InputManager.setTouchAxes`.
 *
 *  - Steer is a soft proportional curve on the heading error: the car asks for the yaw rate that
 *    closes the error in about half a second (`steerForError`), as a share of the yaw full lock gives
 *    at this speed. A small error is a small steer, full lock only comes at 15° (standstill) to 35°
 *    (speed) of error, and the car's own yaw is damped so a corner ends without a weave.
 *  - The thumb's heading is low-pass filtered (`HEAD_SMOOTH_*`, quicker at full deflection), so the
 *    5° a single pixel is worth near the dead zone does not reach the wheel.
 *  - Road alignment is a gentle pull (`alignToRoad`): within ROAD_PULL_ANGLE of the road the wanted
 *    heading leans towards it, most at exactly parallel, nothing at the edge — a lane change is never
 *    fought.
 *  - The stick pointing (nearly) straight back while rolling forwards is ambiguous, so the steer fades
 *    to straight over the last BEHIND_FADE of error instead of slamming to whichever lock the wrap picked.
 *  - Reverse: BRAKE brakes; once the car has stopped and BRAKE is still held REVERSE_DWELL later it
 *    reverses, whatever the stick says. Reversing, the stick points where the TAIL should go (the car as
 *    a whole moves towards the thumb); released, or pointing ahead of the car, it backs straight. GAS
 *    while rolling back brakes out of it with the nose swinging to the stick (a J-turn).
 *  - Stuck assist (assists on): GAS held STUCK_AFTER against static world geometry at a standstill
 *    backs the car off BACKOFF_DIST by itself, the nose turning off the wall, through the same throttle
 *    and steer a thumb would give (online-safe). Never against cars or people (the touch layer only
 *    reports a blocked nose for statics).
 */

export interface DriveFacts {
  /** Car heading: forward = (sin h, cos h). */
  heading: number;
  /** Signed speed along the car's nose, m/s (negative while reversing). */
  forwardSpeed: number;
  /** Ground speed, m/s. */
  speed: number;
  /** Body yaw rate (rad/s, + = turning left, the physics convention). */
  yawRate: number;
  /** Ground unit vectors of the screen's up and right. */
  screenUp: { x: number; z: number };
  screenRight: { x: number; z: number };
  /** Yaw rate (rad/s) full lock gives at this speed (vehicles/model.ts `fullLockYawRate`). */
  fullLockYaw: number;
  /** Heading (rad) of the road under the car (either direction), null off the road / in a junction box. */
  roadAxis?: number | null;
  /** Car position (m), for the stuck assist's distance. */
  x?: number;
  z?: number;
  /**
   * Static world geometry right in front of the bumper (the touch layer's ray probe): `lateral` is the
   * wall normal's share along the car's right (+ = the wall faces the car's right, so the open side is
   * there). Null / undefined when nothing static is close, or something dynamic (a car, a ped) is.
   */
  noseBlocked?: { lateral: number } | null;
}

export interface DriveOptions {
  /** 0.5..1.5: how quickly the car answers the thumb (scales the aim's time constant). */
  sensitivity: number;
  /** Driving assists: road alignment and hold, the stuck assist. */
  assists: boolean;
}

export const DEFAULT_DRIVE_OPTIONS: DriveOptions = { sensitivity: 1, assists: true };

export interface DriveAxes {
  throttle: number;
  steer: number;
  /** Reverse engaged: reverse lights / the REV badge. */
  reverse: boolean;
  /** The stuck assist is backing the car off a wall. */
  backingOff: boolean;
}

/** Seconds the aim takes to close a heading error at a crawl and at speed (ramped between 8 and 25 m/s). */
export const AIM_TAU_SLOW = 0.3;
export const AIM_TAU_FAST = 0.42;
/** Share of the yaw still missing (or overshooting) added to the ask: the wheel unwinds before the nose arrives. */
export const AIM_DAMP = 0.5;
/** Full-lock yaw floor (rad/s) so a stopped car does not ask for infinite lock. */
export const AIM_MIN_YAW = 0.9;
/** Response exponent on the steer: finer near the centre, full at the rim. */
export const STEER_CURVE = 1.25;
/** Heading error (rad, 0.5°) under which the thumb reads as dead straight. */
export const STEER_DEAD_BAND = 0.0087;
/** Thumb heading low-pass (1/s) at the dead zone and at full deflection. */
export const HEAD_SMOOTH_MIN = 18;
export const HEAD_SMOOTH_MAX = 36;
/** Road alignment: the pull's reach (rad, 14°) and its strength at exactly parallel. */
export const ROAD_PULL_ANGLE = 0.244;
export const ROAD_PULL = 0.6;
/** Road hold (thumb off the stick, on a road, above ROAD_HOLD_SPEED): the aim's time constant and reach. */
export const ROAD_HOLD_SPEED = 5;
export const ROAD_HOLD_TAU = 1.2;
export const ROAD_HOLD_ANGLE = 0.28;
/**
 * Stick behind a car rolling forwards: the steer fades (squared) to straight over the last BEHIND_FADE
 * (rad, 30°) to 180° — unless the car is already turning that way: a thumb that was off to one side
 * past BEHIND_ARM_ANGLE (rad, 60°) with the car yawing the same way faster than BEHIND_ARM_YAW (rad/s)
 * keeps that side through the cone, so a U-turn comes round with the thumb straight back.
 */
export const BEHIND_FADE = 0.52;
export const BEHIND_ARM_ANGLE = 1.05;
export const BEHIND_ARM_YAW = 0.25;
/** BRAKE held: the tail mapping fades in over this speed band (m/s) above REVERSE_STOP_SPEED, so the stop never flips the wheel. */
export const TAIL_BLEND_SPEED = 1.0;
/** Stick lifted: the steer ask eases to the road hold / centre at this rate (1/s), a wheel self-centring rather than snapping. */
export const RELEASE_SMOOTH = 10;
/** Throttle rise (1/s) towards drive, forwards and in reverse; lifting off and braking are instant. */
export const THROTTLE_RISE = 8;
export const REVERSE_RISE = 4;
/** Reversing: a stick more than TAIL_STRAIGHT (90°) off the tail backs straight; the steer fades from TAIL_FADE_FROM (70°). */
export const TAIL_STRAIGHT = Math.PI / 2;
export const TAIL_FADE_FROM = 1.22;
/** Reversing, the aim closes the tail's error this much slower than the nose's: a crawl backwards never twitches at 5°. */
export const REVERSE_TAU_SCALE = 1.5;
/** Reverse engages after BRAKE has been held this long (s) with the car under REVERSE_STOP_SPEED (m/s). */
export const REVERSE_DWELL = 0.3;
export const REVERSE_STOP_SPEED = 0.5;
/** Stuck assist: GAS held this long (s) at a standstill against a wall; how far it backs off (m), at most this long (s). */
export const STUCK_AFTER = 0.6;
export const STUCK_SPEED = 0.35;
export const BACKOFF_DIST = 1.8;
export const BACKOFF_MAX_S = 1.6;
export const BACKOFF_STEER = 0.85;
export const BACKOFF_COOLDOWN = 0.8;

export function wrapAngle(a: number): number {
  a = (a + Math.PI) % (Math.PI * 2);
  if (a < 0) a += Math.PI * 2;
  return a - Math.PI;
}

/** Seconds the aim takes to close a heading error at `speed` (m/s). */
export function aimTau(speed: number, sensitivity = 1): number {
  return lerp(AIM_TAU_SLOW, AIM_TAU_FAST, smoothstep(8, 25, Math.abs(speed))) / clamp(sensitivity, 0.5, 1.5);
}

/**
 * Heading error (rad, + = left) -> steer (-1..1, + = right) for a car rolling forwards: the yaw rate
 * that closes the error in `aimTau` seconds, damped by the yaw already there, as a share of full lock,
 * through the response curve. `fullLockYaw` from vehicles/model.ts `fullLockYawRate` (0 at a standstill).
 */
export function steerForError(err: number, yawRate: number, fullLockYaw: number, speed: number, sensitivity = 1, tau = aimTau(speed, sensitivity)): number {
  const a = Math.max(0, Math.abs(err) - STEER_DEAD_BAND);
  if (a === 0 && yawRate === 0) return 0;
  const want = (Math.sign(err) * a) / tau;
  const cmd = want + AIM_DAMP * (want - yawRate);
  const u = clamp(cmd / Math.max(AIM_MIN_YAW, fullLockYaw), -1, 1);
  return -Math.sign(u) * Math.pow(Math.abs(u), STEER_CURVE) || 0;
}

/**
 * Heading `want` leaned towards the road's axis (either direction) when within ROAD_PULL_ANGLE of it:
 * ROAD_PULL of the way at exactly parallel, fading to nothing at the edge. Smooth, so a deliberate
 * diagonal or lane change is never fought and never snaps.
 */
export function alignToRoad(want: number, axis: number | null | undefined): number {
  if (axis === null || axis === undefined) return want;
  let d = wrapAngle(axis - want);
  if (d > Math.PI / 2) d -= Math.PI;
  else if (d < -Math.PI / 2) d += Math.PI;
  const a = Math.abs(d);
  if (a >= ROAD_PULL_ANGLE) return want;
  const t = 1 - a / ROAD_PULL_ANGLE;
  return want + d * ROAD_PULL * t * t;
}

/**
 * Thumb off the stick on a road at speed: the steer that eases a car within ROAD_HOLD_ANGLE of the
 * road's direction parallel to it (null when it does not apply). Gentle: the aim's slow time constant.
 */
export function roadHoldSteer(heading: number, yawRate: number, speed: number, axis: number | null | undefined, fullLockYaw: number): number | null {
  if (axis === null || axis === undefined || speed < ROAD_HOLD_SPEED) return null;
  let d = wrapAngle(axis - heading);
  if (d > Math.PI / 2) d -= Math.PI;
  else if (d < -Math.PI / 2) d += Math.PI;
  if (Math.abs(d) >= ROAD_HOLD_ANGLE) return null;
  return steerForError(d, yawRate, fullLockYaw, speed, 1, ROAD_HOLD_TAU);
}

export type Pedal = 'gas' | 'brake' | null;

interface Backoff {
  x: number;
  z: number;
  /** + = the nose swings left while backing. */
  noseLeft: number;
  t: number;
}

/**
 * The scheme's memory between frames: the thumb's filtered heading, the side a U-turn is going round,
 * the reverse latch, the stuck assist, and the last asks (the release easing and the throttle rise).
 */
export class TouchDrive {
  private wantX = 0;
  private wantZ = 0;
  private stickWas = false;
  /** The side (+ = left) the thumb came from while it sits in the behind cone; 0 = none, fade to straight. */
  private behindSign = 0;
  private brakeDwell = 0;
  private reverseLatched = false;
  private stuckFor = 0;
  private backoff: Backoff | null = null;
  private cooldown = 0;
  private steerOut = 0;
  private throttleOut = 0;

  /** Reverse is engaged (the badge and the lights). */
  get reversing(): boolean {
    return this.reverseLatched || this.backoff !== null;
  }

  /** The thumb's filtered heading (rad), for a read-out; NaN with the thumb off the stick. */
  get wantHeading(): number {
    return this.stickWas ? Math.atan2(this.wantX, this.wantZ) : NaN;
  }

  reset(): void {
    this.stickWas = false;
    this.behindSign = 0;
    this.brakeDwell = 0;
    this.reverseLatched = false;
    this.stuckFor = 0;
    this.backoff = null;
    this.cooldown = 0;
    this.steerOut = 0;
    this.throttleOut = 0;
  }

  step(stick: StickVector, pedal: Pedal, f: DriveFacts, dt: number, o: DriveOptions = DEFAULT_DRIVE_OPTIONS): DriveAxes {
    const gas = pedal === 'gas';
    const brake = pedal === 'brake';
    const vF = f.forwardSpeed;
    const stickOn = stick.mag > 0;

    // ---- the thumb's heading, filtered (snapped on the first frame of a new push)
    if (stickOn) {
      const wx = f.screenRight.x * stick.x + f.screenUp.x * stick.y;
      const wz = f.screenRight.z * stick.x + f.screenUp.z * stick.y;
      const m = Math.hypot(wx, wz) || 1;
      const rx = wx / m;
      const rz = wz / m;
      if (!this.stickWas) {
        this.wantX = rx;
        this.wantZ = rz;
        this.behindSign = 0;
      } else {
        const k = damp(lerp(HEAD_SMOOTH_MIN, HEAD_SMOOTH_MAX, stick.mag), dt);
        this.wantX += (rx - this.wantX) * k;
        this.wantZ += (rz - this.wantZ) * k;
        const n = Math.hypot(this.wantX, this.wantZ);
        if (n < 1e-3) {
          this.wantX = rx;
          this.wantZ = rz;
        } else {
          this.wantX /= n;
          this.wantZ /= n;
        }
      }
    } else this.behindSign = 0;
    this.stickWas = stickOn;
    const raw = stickOn ? Math.atan2(this.wantX, this.wantZ) : f.heading;
    const want = stickOn && o.assists ? alignToRoad(raw, f.roadAxis) : raw;

    // ---- throttle and the reverse latch: BRAKE brakes, then (stopped, still held) reverses
    let throttle = 0;
    if (brake) {
      if (vF > REVERSE_STOP_SPEED) {
        this.brakeDwell = 0;
        throttle = -1;
      } else if (vF < -REVERSE_STOP_SPEED || this.reverseLatched) {
        this.reverseLatched = true;
        throttle = -1;
      } else {
        this.brakeDwell += dt;
        if (this.brakeDwell >= REVERSE_DWELL) {
          this.reverseLatched = true;
          throttle = -1;
        }
      }
    } else {
      this.brakeDwell = 0;
      this.reverseLatched = false;
      throttle = gas ? 1 : 0;
    }

    // ---- the stuck assist: backing off by itself, or counting up to it
    this.cooldown = Math.max(0, this.cooldown - dt);
    if (this.backoff) {
      const b = this.backoff;
      b.t += dt;
      const moved = f.x !== undefined && f.z !== undefined ? Math.hypot(f.x - b.x, f.z - b.z) : 0;
      if (brake || moved >= BACKOFF_DIST || b.t >= BACKOFF_MAX_S || !o.assists) {
        this.backoff = null;
        this.cooldown = BACKOFF_COOLDOWN;
      } else {
        // Rolling back, right steer (+) swings the body anticlockwise: the nose goes left.
        return this.out(-1, b.noseLeft * BACKOFF_STEER, vF, dt, true, true);
      }
    }
    if (o.assists && gas && f.noseBlocked && Math.abs(vF) < STUCK_SPEED && f.speed < STUCK_SPEED && this.cooldown <= 0) {
      this.stuckFor += dt;
      if (this.stuckFor >= STUCK_AFTER) {
        this.stuckFor = 0;
        const delta = stickOn ? wrapAngle(want - f.heading) : 0;
        // The nose turns towards the thumb; without one, away from the wall's open side's opposite.
        const noseLeft = stickOn && Math.abs(delta) > 0.1 ? Math.sign(delta) : f.noseBlocked.lateral > 0.05 ? -1 : 1;
        this.backoff = { x: f.x ?? 0, z: f.z ?? 0, noseLeft, t: 0 };
        return this.out(-1, noseLeft * BACKOFF_STEER, vF, dt, true, true);
      }
    } else this.stuckFor = 0;

    // ---- steer
    const rollingBack = vF < -REVERSE_STOP_SPEED;
    // The tail mapping: reverse engaged, coasting back without GAS, or BRAKE held at (nearly) a stop —
    // the wheels turn to where the tail should go during the dwell, so the car backs off without a flip.
    // It fades in over TAIL_BLEND_SPEED above the stop speed, so the stop itself never jumps the wheel.
    let tailMix = 0;
    if (this.reverseLatched || (rollingBack && !gas)) tailMix = 1;
    else if (brake) tailMix = clamp(1 - (vF - REVERSE_STOP_SPEED) / TAIL_BLEND_SPEED, 0, 1);
    let steer = 0;
    if (!stickOn) {
      // Thumb lifted: ease to the road hold (or centre) rather than snap, like a wheel self-centring.
      const hold = tailMix === 0 && o.assists ? roadHoldSteer(f.heading, f.yawRate, f.speed, f.roadAxis, f.fullLockYaw) : null;
      this.steerOut += ((hold ?? 0) - this.steerOut) * damp(RELEASE_SMOOTH, dt);
      if (Math.abs(this.steerOut) < 1e-4) this.steerOut = 0;
      return this.out(throttle, this.steerOut, vF, dt, this.reverseLatched, false);
    }
    if (tailMix > 0) {
      // The tail chases the stick. Rolling back the wheels' effect is flipped, so the tail's heading
      // error maps straight onto the steer; a stick ahead of the car means "straight back".
      const back = wrapAngle(want - (f.heading + Math.PI));
      const a = Math.abs(back);
      if (a < TAIL_STRAIGHT) {
        const k = a <= TAIL_FADE_FROM ? 1 : (TAIL_STRAIGHT - a) / (TAIL_STRAIGHT - TAIL_FADE_FROM);
        steer = -steerForError(back, f.yawRate, f.fullLockYaw, f.speed, o.sensitivity, aimTau(f.speed, o.sensitivity) * REVERSE_TAU_SCALE) * k;
      }
    }
    if (tailMix < 1) {
      const delta = wrapAngle(want - f.heading);
      const a = Math.abs(delta);
      let fwd: number;
      if (a <= Math.PI - BEHIND_FADE) {
        // Outside the behind cone: remember which side the thumb is on once the car is turning that way.
        this.behindSign = a > BEHIND_ARM_ANGLE && Math.sign(f.yawRate) === Math.sign(delta) && Math.abs(f.yawRate) > BEHIND_ARM_YAW ? Math.sign(delta) : 0;
        fwd = steerForError(delta, f.yawRate, f.fullLockYaw, f.speed, o.sensitivity);
      } else if (this.behindSign !== 0) {
        // A U-turn in progress: keep going round the same way, full error, no fade.
        fwd = steerForError(this.behindSign * a, f.yawRate, f.fullLockYaw, f.speed, o.sensitivity);
      } else {
        // Pointing (nearly) straight back from nothing is ambiguous: ease to straight rather than slam to a lock.
        const t = (Math.PI - a) / BEHIND_FADE;
        fwd = steerForError(delta, f.yawRate, f.fullLockYaw, f.speed, o.sensitivity) * t * t;
      }
      // Rolling backwards under GAS (a J-turn out of a reverse) the wheels' effect is flipped; the sense
      // crosses over through the last REVERSE_STOP_SPEED of the roll, so the stop never steps the wheel.
      const sense = vF >= 0 ? 1 : 1 - 2 * Math.min(1, -vF / REVERSE_STOP_SPEED);
      steer = steer * tailMix + fwd * sense * (1 - tailMix);
    }
    return this.out(throttle, steer, vF, dt, this.reverseLatched, false);
  }

  /**
   * The asks, remembered: the steer for the release easing, the throttle rate-limited towards drive
   * (THROTTLE_RISE forwards, REVERSE_RISE backwards) — lifting off and braking against the roll are instant.
   */
  private out(throttle: number, steer: number, vF: number, dt: number, reverse: boolean, backingOff: boolean): DriveAxes {
    this.steerOut = steer;
    const braking = (throttle < 0 && vF > REVERSE_STOP_SPEED) || (throttle > 0 && vF < -REVERSE_STOP_SPEED);
    if (!braking && Math.abs(throttle) > Math.abs(this.throttleOut) && Math.sign(throttle) === Math.sign(this.throttleOut || throttle)) {
      const rise = throttle > 0 ? THROTTLE_RISE : REVERSE_RISE;
      throttle = Math.sign(throttle) * Math.min(Math.abs(throttle), Math.abs(this.throttleOut) + rise * dt);
    }
    this.throttleOut = throttle;
    return { throttle, steer, reverse, backingOff };
  }
}
